import { useCallback, useEffect, useRef, useState } from 'react';
import { staticFile } from 'remotion';
import { catalogSoundEffectSource } from '@waka/compat/catalog-sound-effect-source';
import { PlayArrowFill, PauseFill, MusicNote } from '@crabs-ai/web-icons';
import { cn, toast } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { PlayerRef } from '@remotion/player';
import { SoundEffectDefinition } from '@/features/remotion-editor/scripts/sound-effect-data';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '../../../../utils/use-context';
import { addAsset } from '../../../../assets/add-asset-wkwk';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';
import { CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import {
  DtoContentType,
  DtoExtension,
  DtoProjectType,
} from '@/shared/constants';
import {
  ASSET_DRAG_TYPE,
  AssetDragData,
  setCurrentDragData,
} from '../asset/AssetContentItem';

// 전역 재생 이벤트 (다른 효과음 정지용)
const SOUND_EFFECT_PLAY_EVENT = 'sound-effect-item-play';

interface SoundEffectItemProps {
  item: SoundEffectDefinition;
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function SoundEffectItem({
  item,
  playerRef,
}: SoundEffectItemProps) {
  const t = useTranslations();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const itemIdRef = useRef(item.id);

  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  // duration 포맷 (ms -> 초)
  const formatDuration = (ms: number) => {
    const seconds = ms / 1000;
    return `${seconds.toFixed(1)}s`;
  };

  // 다른 효과음 재생 시 현재 재생 중지
  useEffect(() => {
    const handleOtherPlay = (e: CustomEvent<string>) => {
      if (e.detail !== itemIdRef.current && isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      }
    };

    window.addEventListener(
      SOUND_EFFECT_PLAY_EVENT,
      handleOtherPlay as EventListener,
    );
    return () => {
      window.removeEventListener(
        SOUND_EFFECT_PLAY_EVENT,
        handleOtherPlay as EventListener,
      );
    };
  }, [isPlaying]);

  // 재생/정지 토글
  const handlePlayToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          // 다른 효과음 정지 이벤트 발송
          window.dispatchEvent(
            new CustomEvent(SOUND_EFFECT_PLAY_EVENT, { detail: item.id }),
          );
          audioRef.current.currentTime = 0;
          audioRef.current
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        }
      }
    },
    [isPlaying, item.id],
  );

  // 재생 종료 핸들러
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // 드래그 시작 - ASSET_DRAG_TYPE 형식으로 데이터 전달 (기존 DropHandler 재활용)
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // 이전 드래그 데이터 초기화
      setCurrentDragData(null);

      const audioUrl = staticFile(item.path);
      const displayName = t(`SoundEffects.${item.id}`);

      // AssetDragData 형식으로 변환 (기존 DropHandler와 호환)
      const dragData: AssetDragData = {
        itemId: Date.now(), // 고유 ID
        name: displayName,
        publicUrl: audioUrl,
        thumbnailPublicUrl: null,
        contentType: DtoContentType.audio,
        duration: item.duration,
        extension: item.path.endsWith('.wav') ? DtoExtension.wav : DtoExtension.mp3,
        staticPath: item.path, // public 폴더 내 정적 파일 경로
      };

      e.dataTransfer.setData(ASSET_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);

      // 전역 드래그 데이터 설정 (dragover에서 사용)
      setCurrentDragData(dragData);

      // 드래그 이미지
      const dragElement = document.createElement('div');
      dragElement.textContent = displayName;
      dragElement.style.cssText = `
        position: absolute; top: -9999px; left: -9999px;
        padding: 8px 12px; background: rgba(0, 0, 0, 0.8);
        color: white; font-size: 12px; font-weight: 500;
        border-radius: 6px; white-space: nowrap;
        max-width: 200px; overflow: hidden; text-overflow: ellipsis;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      `;
      document.body.appendChild(dragElement);
      e.dataTransfer.setDragImage(dragElement, 0, 0);

      // 드래그 이미지 설정 후 DOM에서 제거
      requestAnimationFrame(() => {
        document.body.removeChild(dragElement);
      });
    },
    [item, t],
  );

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setCurrentDragData(null);
  }, []);

  // 클릭 시 타임라인에 추가 (현재 playhead 위치에)
  const handleClick = useCallback(async () => {
    const filename = `${t(`SoundEffects.${item.id}`)}.${item.path.endsWith('.wav') ? 'wav' : 'mp3'}`;
    try {
      const documentAtClick = stateAsRef.current.undoableState;
      const audioUrl = staticFile(item.path);
      const response = await fetch(audioUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const durableSource = await catalogSoundEffectSource(item.path, blob);
      if (stateAsRef.current.undoableState !== documentAtClick)
        throw new Error('편집 내용이 바뀌었습니다. 효과음을 다시 선택해 주세요.');

      const state = stateAsRef.current;
      const tracks = state.undoableState.tracks;
      const fps = state.undoableState.fps;
      const compositionWidth = state.undoableState.compositionWidth;
      const compositionHeight = state.undoableState.compositionHeight;

      // 현재 playhead 위치 가져오기
      const currentFrame = playerRef.current?.getCurrentFrame() ?? 0;

      await addAsset({
        file: blob,
        contentId: `sound-effect-${item.id}`,
        filename,
        clip: {
          clipType: CLIP_TYPES.AUDIO,
          contentPublicUri: durableSource,
          videoStart: 0,
          videoEnd: item.duration,
        },
        timelineWriteContext,
        playerRef,
        dropPosition: null,
        fps,
        compositionWidth,
        compositionHeight,
        tracks,
        projectType: DtoProjectType.spotlight,
        isAsset: true,
        extension: item.path.split('.').pop() || 'mp3',
        timelineStartFrame: currentFrame, // playhead 위치에 추가
        staticPath: item.path, // public 폴더 내 정적 파일 경로
        expectedUndoableState: documentAtClick,
      });

      toast({
        type: 'success',
        description: t('Editor.sound-effect.add-success'),
        toastOptions: { position: 'top-center' },
      });
    } catch (error) {
      console.error('[SoundEffectItem] addAsset failed:', error);
      showUploadFailureToast(
        t,
        classifyUploadFailure(error, filename),
      );
    }
  }, [item, playerRef, stateAsRef, t, timelineWriteContext]);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      className={cn(
        'flex cursor-grab items-center gap-xs rounded-sm px-sm py-xs',
        'bg-sys-style-neutral-lightest transition-colors hover:bg-sys-style-neutral-light',
        'active:cursor-grabbing',
        isDragging && 'opacity-50',
      )}
    >
      {/* 오디오 아이콘 */}
      <MusicNote className="size-md shrink-0 [&_path]:fill-sys-cont-neutral-default" />

      {/* 제목 */}
      <span className="flex-1 truncate text-comp-moderate-medium text-sys-cont-neutral-default">
        {t(`SoundEffects.${item.id}`)}
      </span>

      {/* 재생/정지 버튼 */}
      <button
        type="button"
        onClick={handlePlayToggle}
        className="flex size-lg items-center justify-center rounded-full transition-colors hover:bg-sys-style-neutral-default"
      >
        {isPlaying ? (
          <PauseFill className="size-md [&_path]:fill-sys-cont-primary-default" />
        ) : (
          <PlayArrowFill className="size-md [&_path]:fill-sys-cont-neutral-default" />
        )}
      </button>

      {/* 길이 */}
      <span className="text-sys-cont-neutral-tertiary min-w-[40px] text-right text-comp-accent-small">
        {formatDuration(item.duration)}
      </span>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={staticFile(item.path)}
        preload="none"
        onEnded={handleEnded}
      />
    </div>
  );
}
